import type { CaseFile } from '@/modules/Cases/types'
import type { NavigationContext } from '@/modules/Project/Navigation/navigationContext'
import { type NavigationParams } from '@/modules/Project/Navigation/navigationTypes'
import { useProjectNavigator } from '@/modules/Project/Navigation/useProjectNavigator'
import { useEntity } from '@/modules/Project/useEntity'
import type { AtLeast } from '@/shared/types'
import { ref, type Ref } from 'vue'
import { useCaseSidebarRoutingToggle } from './useCaseSidebarRoutingToggle'

export type CaseNavigationCrumb =
  | { type: 'root'; isActive: boolean }
  | { type: 'project'; params: NavigationParams; isActive: boolean }
  | { type: 'file'; file: CaseFile; isActive: boolean }

export type CaseNavigationContext = NavigationContext & {
  caseCrumbs: Ref<AtLeast<CaseNavigationCrumb, 1>>
  openFileAndSidebar: (file: CaseFile) => void
}

/**
 * Encapsulates base project navigation logic with the special behavior of the case sidebar navigation
 *
 * - the breadcrumbs start with an "all data" crumb
 * - in case of an entity, the breadcrumbs end with a "jump to property" component
 * - the breadcrumbs could lead directly to a file
 *
 * Because core compatibility must be preservedf, the special case breadcrumbs are stored in a separate ref.
 */
export const useCaseSidebarNavigator = (): CaseNavigationContext => {
  const entityStore = useEntity()

  const navigator = useProjectNavigator()

  const { isSidebarOpen, openSidebar } = useCaseSidebarRoutingToggle()

  const caseCrumbs = ref<AtLeast<CaseNavigationCrumb, 1>>([{ type: 'root', isActive: true }])

  const navigate = (params: NavigationParams) => {
    const nextStack: AtLeast<CaseNavigationCrumb, 1> = [{ type: 'root', isActive: false }]

    navigator.navigate(params)
    navigator.breadcrumbs.value.forEach((params, i) => {
      const isActive = i === navigator.breadcrumbs.value.length - 1
      nextStack.push({ type: 'project', params, isActive })
    })

    caseCrumbs.value = nextStack

    if (!isSidebarOpen.value) {
      openSidebar()
    }
  }

  const openFileAndSidebar = (file: CaseFile) => {
    caseCrumbs.value = [
      { type: 'root', isActive: false },
      { type: 'file', file, isActive: true },
    ]
    if (!isSidebarOpen.value) {
      openSidebar()
    }
  }

  const reset = () => {
    navigator.reset()
    caseCrumbs.value = [{ type: 'root', isActive: true }]
    entityStore.setEntityId(undefined)
  }

  return {
    ...navigator,
    navigate,
    caseCrumbs,
    openFileAndSidebar,
    reset,
  }
}
